import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, Grid, IconButton, TextField, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Editor } from '../Editor/Editor';
import { ButtonCustom } from '../Button/ButtonCustom';
import { closeIcon, flexcol, flexItemCenter, flexItemRight, flexItemSpaceBetween } from '../../global';
import { Event, EventCloseout, EventStatus } from '../../services/Types';
import PDFFileIcon from '../../images/ViewAndEdit/PDFFileIcon.svg'
import CSVFileIcon from '../../images/ViewAndEdit/CSVFileIcon.svg'
import PlusIcon from '../../images/ViewAndEdit/PlusIcon.svg'
import EditIcon from '../../images/ViewAndEdit/EditIcon.svg'
import CrossIcon from '../../images/ViewAndEdit/CrossIcon.svg'
import { createEventCloseout, updateEventCloseout } from '../../services/EventService'

interface DialogCloseoutProps {
  event: Event;
  onClose: () => void;
}

export const DialogCloseout: React.FC<DialogCloseoutProps> = ({
  event,
  onClose,
}) => {
  // Drag And Drop
  const [isDragging, setIsDragging] = useState(false)
  const [files, setFiles] = useState<any[]>([])
  const [fileError, setFileError] = useState('')
  const [newEventCloseout, setNewEventCloseout] = useState<EventCloseout>(
    (event.eventCloseout || {
      eventId: event.id,
      files: null,
      highlightsActionItems: '',
      id: 0,
      numberOfAttendees: 0,
      numberOfRegistrants: 0,
      participantRatingsSummary: ''
    } as EventCloseout)
  )
  const [isProcessing, setIsProcessing] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const handleDragOver = (e: { preventDefault: () => void }) => {
    e.preventDefault()
    setIsDragging(true)
  }
  const handleDragLeave = () => {
    setIsDragging(false)
  }
  const handleDrop = (e: {
    preventDefault: () => void
    dataTransfer: { files: Iterable<unknown> | ArrayLike<unknown> }
  }) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFiles: any = Array.from(e.dataTransfer.files)
    let fileError = ''
    for (let i = 0; i < droppedFiles.length; i++) {
      if (droppedFiles[i].size > 20 * 1000 * 1000) {
        fileError = 'Maximum size allowed is 20 MB'
        break
      }
    }
    setFileError(fileError)
    if (!fileError) setFiles([...files, ...droppedFiles])
  }

  const handleFileInputChange = (e: any) => {
    const newFiles = e.target.files
    let fileError = ''
    for (let i = 0; i < newFiles.length; i++) {
      if (newFiles[i].size > 20 * 1000 * 1000) {
        fileError = 'Maximum size allowed is 20 MB'
        break
      }
    }
    setFileError(fileError)
    if (!fileError) setFiles([...files, ...newFiles])
  }

  const removeFile = (file: string | number) => () => {
    if (typeof file === 'string') {
      setNewEventCloseout({
        ...newEventCloseout,
        files: newEventCloseout.files?.filter((x: string) => x !== file) || newEventCloseout.files
      })
    } else {
      setFiles(files.filter((_, i) => i !== file))
    }
  }

  const handleSubmit = async () => {
    const {
      id,
      eventId,
      numberOfRegistrants,
      numberOfAttendees,
      highlightsActionItems,
      participantRatingsSummary
    } = newEventCloseout
    console.log('newEventCloseout', newEventCloseout)
    console.log('files', files)
    setIsProcessing(true)

    let response
    if (id) {
      response = await updateEventCloseout(
        id,
        numberOfRegistrants,
        numberOfAttendees,
        highlightsActionItems,
        participantRatingsSummary,
        JSON.stringify(newEventCloseout.files),
        files
      )
      if (response.data.statusCode === 200) {
        console.log('Event closeout updated successfully')
        handleClose()
      } else {
        console.log('Error updating event closeout')
      }
    } else {
      response = await createEventCloseout(
        eventId,
        numberOfRegistrants,
        numberOfAttendees,
        highlightsActionItems,
        participantRatingsSummary,
        files
      )
      if (response.data.statusCode === 200) {
        console.log('Event closeout created successfully')
        handleClose()
      } else {
        console.log('Error creating event closeout')
      }
    }
  }

  const handleChangeHighlightsActionItems = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const text = event.target.value;
    const highlightsActionItems = text?.trim()?.replace(/<[^>]+>/g, '') || '';

    setNewEventCloseout({
      ...newEventCloseout,
      highlightsActionItems
    });
  };

  const handleChangeParticipantRatingsSummary = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const text = event.target.value;
    const participantRatingsSummary = text?.trim()?.replace(/<[^>]+>/g, '') || '';

    setNewEventCloseout({
      ...newEventCloseout,
      participantRatingsSummary
    });
  };

  const handleClose = () => {
    if (!isProcessing) {
      setIsOpen(false)
      setIsProcessing(false)
      onClose()
    }
  }

  const handleChangeNumberOfRegistrants = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const text = event.target.value;
    const numberOfRegistrants = text?.trim()?.replace(/<[^>]+>/g, '') || '';
    console.log('numberOfRegistrants', numberOfRegistrants)

    setNewEventCloseout({
      ...newEventCloseout,
      numberOfRegistrants: parseInt(numberOfRegistrants)
    });
  };

  const handleChangeNumberOfAttendees = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const text = event.target.value;
    const numberOfAttendees = text?.trim()?.replace(/<[^>]+>/g, '') || '';
    console.log('numberOfAttendees', numberOfAttendees)

    setNewEventCloseout({
      ...newEventCloseout,
      numberOfAttendees: parseInt(numberOfAttendees)
    });
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      maxWidth='lg'
      PaperProps={{
        style: {
          borderRadius: '24px',
          padding: '32px'
        }
    }}
    >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        mb: '20px'
      }}
    >
      <Typography
        sx={{
          fontSize: '24px',
          fontWeight: '500',
          width: '90%'
        }}
      >
        {event.title}
      </Typography>
      <CloseRoundedIcon
        onClick={handleClose}
        style={closeIcon}
        sx={{
          fontSize: 20,
          color: '#253D82',
          fontWeight: 200,
          cursor: 'pointer'
        }}
      />
    </Box>

    <Grid item lg={12}>
      <Typography
        sx={{
          fontSize: '18px',
          fontWeight: '500',
          lineHeight: '30.6pxpx',
          mb: '14px'
        }}
      >
        Highlights and action items
      </Typography>
    </Grid>

    <Grid item lg={12}>
      <Grid
        item
        sx={{
          display: 'flex',
          flexDirection: {
            lg: 'row',
            md: 'column',
            sm: 'column',
            xs: 'column'
          },
          justifyContent: 'space-between',
          gap: { lg: '76px', md: '50px', xs: '50px' }
        }}
      >
        <Grid
          item
          lg={6}
          md={12}
          style={flexcol}
          sx={{ gap: '24px', mt: '8px' }}
        >
          <Box
            sx={{
              width: { lg: '610px', md: '100%' },
              gap: '16px'
            }}
          >
            {newEventCloseout?.highlightsActionItems ? (
              <Editor
                setEditorText={handleChangeHighlightsActionItems}
                editorText={newEventCloseout?.highlightsActionItems}
              />
            ) : (
              <Box
                sx={{
                  background: '#F5F5F9',
                  borderRadius: '16px',
                  height: '184px',
                  py: '16px'
                }}
              >
                <Box style={flexItemSpaceBetween} sx={{ px: '16px' }}>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      fontWeight: '500',
                      textAlign: 'left'
                    }}
                  >
                    Highlights and action items
                  </Typography>
                  <IconButton onClick={() => setIsOpen(true)} size="large">
                    <Box component='img' src={EditIcon} alt='EditIcon ' />
                  </IconButton>
                </Box>
                <Box sx={{ ml: '-10px', mr: '10px', mt: '-10px' }}>
                  <TextField
                    inputRef={input => newEventCloseout?.highlightsActionItems && input?.focus()}
                    multiline
                    rows={4}
                    disabled={!newEventCloseout?.highlightsActionItems}
                    fullWidth
                    sx={{
                      '& fieldset': { border: 'none' },
                      '.MuiOutlinedInput-root.Mui-focused': {
                        fieldset: { borderColor: '#006BD7' }
                      }
                    }}
                    value={
                      newEventCloseout?.highlightsActionItems?.replace(
                        /<[^>]+>/g,
                        ''
                      ) || ''
                    }
                    onChange={handleChangeHighlightsActionItems}
                  />
                </Box>
              </Box>
            )}
          </Box>

          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: '500',
              lineHeight: '30.6pxpx'
            }}
          >
            Participant ratings summary
          </Typography>

          <Box
            sx={{
              width: { lg: '610px', md: '100%' },
              gap: '16px'
            }}
          >
            {newEventCloseout?.participantRatingsSummary ? (
              <Editor
                setEditorText={handleChangeParticipantRatingsSummary}
                editorText={newEventCloseout?.participantRatingsSummary}
              />
            ) : (
              <Box
                sx={{
                  background: '#F5F5F9',
                  borderRadius: '16px',
                  height: '184px',
                  py: '16px'
                }}
              >
                <Box style={flexItemSpaceBetween} sx={{ px: '16px' }}>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      fontWeight: '500',
                      lineHeight: '24px',
                      letterSpacing: '0.10000000149011612px',
                      textAlign: 'left'
                    }}
                  >
                    Participant ratings summary
                  </Typography>
                  <IconButton onClick={() => setIsOpen(true)} size="large">
                    <Box component='img' src={EditIcon} alt=' ' />
                  </IconButton>
                </Box>
                <Box sx={{ ml: '-10px', mr: '10px' }}>
                  <TextField
                    inputRef={input => newEventCloseout?.participantRatingsSummary && input?.focus()}
                    autoFocus={!!newEventCloseout?.participantRatingsSummary}
                    multiline
                    disabled={!newEventCloseout?.participantRatingsSummary}
                    rows={4}
                    sx={{
                      '& fieldset': { border: 'none' },
                      '.MuiOutlinedInput-root.Mui-focused': {
                        fieldset: { borderColor: '#006BD7' }
                      }
                    }}
                    fullWidth
                    value={
                      newEventCloseout?.participantRatingsSummary?.replace(
                        /<[^>]+>/g,
                        ''
                      ) || ''
                    }
                    onChange={handleChangeParticipantRatingsSummary}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item lg={6} md={12} style={flexcol}>
          <Box
            width='100%'
            sx={{
              display: 'flex',
              flexDirection: {
                lg: 'row',
                md: 'column',
                sm: 'column',
                xs: 'column'
              }
            }}
          >
            <TextField
              sx={{
                width: { lg: '176px', md: '100%' },
                marginLeft: '0!important',
                marginRight: { lg: '40px!important' },
                '.MuiOutlinedInput-root.Mui-focused': {
                  fieldset: { borderColor: '#006BD7' }
                },
                [`& fieldset`]: {
                  borderRadius: '8px'
                }
              }}
              name='Number of Registrants'
              label='Number of Registrants'
              placeholder='Type here'
              value={newEventCloseout?.numberOfRegistrants || ''}
              onChange={handleChangeNumberOfRegistrants}
              size='small'
            />
            <TextField
              sx={{
                width: { lg: '176px', md: '100%' },
                marginLeft: '0!important',
                '.MuiOutlinedInput-root.Mui-focused': {
                  fieldset: { borderColor: '#006BD7' }
                },
                [`& fieldset`]: {
                  borderRadius: '8px'
                }
              }}
              name='Number of Attendees'
              label='Number of Attendees'
              placeholder='Type here'
              value={newEventCloseout?.numberOfAttendees || ''}
              onChange={handleChangeNumberOfAttendees}
              size='small'
            />
          </Box>

          <Box sx={{ mt: '64px' }}>
            <Typography fontWeight='500' fontSize='14px' sx={{ mb: '8px' }}>
              Add files (CSV, PDF)
              {fileError && (
                <Typography
                  component='span'
                  sx={{ color: '#d50000', fontSize: 14, ml: 0.5 }}
                >
                  ({fileError})
                </Typography>
              )}
            </Typography>
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                px: '63px',
                py: '26px',
                borderRadius: '12px',
                backgroundColor: '#F2F2F2',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '24px',
                cursor: 'pointer'
              }}
            >
              <Button
                sx={{
                  width: '84px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  height: '51px'
                }}
                style={flexItemCenter}
                component='label'
              >
                <Box component='img' src={PlusIcon} alt='PlusIcon' />
                <input
                  accept='.pdf,.csv'
                  type='file'
                  multiple
                  hidden
                  onChange={handleFileInputChange}
                />
              </Button>

              <Grid
                item
                sm={12}
                container
                sx={{
                  gap: '8px',
                  gridAutoFlow: 'row',
                  justifyContent: 'center'
                }}
              >
                {(newEventCloseout?.files || []).map(
                  (file: string, index: number) => (
                    <File
                      key={index}
                      file={file}
                      filename={file.substring(file.lastIndexOf('/') + 1)}
                      onRemove={removeFile(file)}
                    />
                  )
                )}
                {files.map((file, index) => (
                  <File
                    key={index}
                    filename={file.name}
                    onRemove={removeFile(index)}
                  />
                ))}
              </Grid>

              <Typography
                sx={{
                  letter: '0.1 px',
                  fontSize: '12px',
                  fontWeight: '500',
                  textAlign: 'center',
                  width: '277px',
                  lineHeight: '14.06px'
                }}
              >
                Add files by clicking here or dragging and dropping your
                file
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Grid>

    <Grid>
      <Box style={flexItemRight}>
        <ButtonCustom
          icon={false}
          backgroundColor='transparent'
          color='#333333'
          onClick={handleClose}
          label='Cancel'
        />
        <ButtonCustom
          icon={false}
          backgroundColor='#333333'
          color='#ffffff'
          onClick={handleSubmit}
          disabled={isProcessing}
          label='Apply'
        />
      </Box>
    </Grid>
  </Dialog>
  );
};

const File = ({
  filename,
  onRemove,
  file
}: {
  filename: string
  onRemove: () => void
  file?: string
}) => (
  <Box
    sx={{
      width: '95px',
      borderRadius: '8px',
      px: '8px',
      py: '12px',
      gap: '8px',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}
    onDoubleClick={() => {
      if (file) window.open(`${process.env.REACT_APP_S3_FILES_URL}${file}`)
    }}
  >
    <Box
      width='60%'
      sx={{
        display: 'flex',
        alignItems: 'flex-start'
      }}
    >
      <Box
        component='img'
        src={filename.toLowerCase().endsWith('pdf') ? PDFFileIcon : CSVFileIcon}
        alt='FileIcon'
      />
      <IconButton
        sx={{
          mt: '-5px',
          ml: '5px',
          width: '8px',
          height: '8px'
        }}
        onClick={onRemove}
        size="large">
        <Box
          component='img'
          src={CrossIcon}
          alt='CrossIcon'
        />
      </IconButton>
    </Box>
    <Typography
      width='100%'
      color='#565656'
      fontSize='12px'
      noWrap
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      {filename}
    </Typography>
  </Box>
)